import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Checkbox, Divider, TextInput } from '@mantine/core';
import { InfoCircle } from 'tabler-icons-react';
import NumberFormat from 'react-number-format';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import ResponsiveModal from '../../common/ResponsiveModal';
import FormSection from '../../common/FormSection';
import {
  CURRENCY_TYPE_LIST,
  PAYMENT_PROVIDERS,
  PAYMENT_PROVIDER_LIST
} from '../../../config/constants';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';

const INITIAL_FORM_STATE = {
  regAssociationPaymentProviderType: PAYMENT_PROVIDERS.stripe.value.toString(),
  accountId: '',
  accountPassword: '',
  connectExisting: false,
  serviceFees: {
    useDefaultServiceFee: true,
    flatRateFee: null,
    percentageRateFee: null,
    rebasePaymentFees: false
  },
  showConfirmRebaseModal: false,
  showEmailMessage: false,
  isLoading: false
};

const FinancialPayeeOnboardingModal = ({
  isOpen,
  onClose,
  regAssociation,
  fkPaymentProviderType
}) => {
  const { onboardPaymentProvider, fetchAdminRegAssociations } = useContext(
    RegistrationAdminContext
  );
  const [formState, setFormState] = useState({ ...INITIAL_FORM_STATE });
  const selectedPaymentProvider = PAYMENT_PROVIDER_LIST.find(
    (p) =>
      p.value.toString() ===
      formState.regAssociationPaymentProviderType?.toString()
  );
  const sportspaySelected =
    selectedPaymentProvider?.value === PAYMENT_PROVIDERS.sportspay.value;

  const currency = CURRENCY_TYPE_LIST.find(
    (c) =>
      c.value.toString() ===
      regAssociation?.regAssociationPaymentProvider?.fkCurrencyType?.toString()
  )?.abbr;

  const showProviderAccountInputFields =
    formState.connectExisting ||
    sportspaySelected ||
    regAssociation?.regAssociationPaymentProvider;

  useEffect(() => {
    if (isOpen && regAssociation) {
      const paymentProvider = regAssociation.regAssociationPaymentProvider;
      if (paymentProvider) {
        setFormState({
          regAssociationPaymentProviderType: paymentProvider.fkPaymentProvider.toString(),
          accountId: regAssociation.regAssociationPaymentProvider.accountId,
          accountPassword: '',
          connectExisting: false,
          serviceFees: {
            useDefaultServiceFee:
              !regAssociation.regAssociationPaymentProvider.percentageRateFee &&
              !regAssociation.regAssociationPaymentProvider.flatRateFee,
            flatRateFee:
              regAssociation.regAssociationPaymentProvider.flatRateFee,
            percentageRateFee:
              regAssociation.regAssociationPaymentProvider.percentageRateFee,
            rebasePaymentFees: false
          },
          showConfirmRebaseModal: false,
          showEmailMessage: false,
          isLoading: false
        });
      }
      else {
        setFormState({
          ...INITIAL_FORM_STATE,
          regAssociationPaymentProviderType:
            fkPaymentProviderType ?? PAYMENT_PROVIDERS.stripe.value.toString()
        });
      }
    }
  }, [isOpen]);

  const onOnboardPaymentProvider = () => {
    setFormState({ ...formState, isLoading: true });
    onboardPaymentProvider(
      regAssociation.pkRegAssociation,
      {
        ...formState,
        rebasePaymentFees:
          sportspaySelected && formState.serviceFees.rebasePaymentFees,
        ...(sportspaySelected
          ? formState.serviceFees.useDefaultServiceFee
            ? { flatRateFee: null, percentageRateFee: null }
            : {
                flatRateFee: formState.serviceFees.flatRateFee,
                percentageRateFee: formState.serviceFees.percentageRateFee
              }
          : {})
      },
      (session) => {
        if (session.sessionUrl) {
          window.location.href = session.sessionUrl;
        }
        else if (session.emailSent) {
          setFormState({
            ...formState,
            showEmailMessage: true,
            isLoading: false
          });
        }
        else {
          triggerNotification('Payment provider onboarded', 'Success', 'green');
          onClose();
          fetchAdminRegAssociations();
        }
      },
      (e) => {
        triggerNotification(e);
        setFormState({ ...formState, isLoading: false });
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={{
        isLoading: formState.isLoading,
        isSubmitDisabled: false,
        onCancel: onClose,
        onSubmit: onOnboardPaymentProvider,
        submitTitle: 'Continue'
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      title={
        !regAssociation?.regAssociationPaymentProvider
          ? `${selectedPaymentProvider?.label || 'Payment Provider'} Onboarding`
          : `Update ${selectedPaymentProvider?.label || 'Payment Provider'}`
      }
    >
      {!selectedPaymentProvider || !regAssociation ? (
        <></>
      ) : formState.showEmailMessage ? (
        <FormSection cancelTitle="Close" isSubmitHidden onCancel={onClose}>
          <Alert
            icon={<InfoCircle />}
            style={{ marginTop: 20 }}
            variant="outline"
          >
            <AppText weight={500}>
              Please follow the onboarding instructions sent to your email
            </AppText>
          </Alert>
        </FormSection>
      ) : (
        <AppStack style={{ gap: 16 }}>
          {!regAssociation.regAssociationPaymentProvider &&
            selectedPaymentProvider.canCreateNewAccounts && (
              <AppStack style={{ gap: 30 }}>
                <Checkbox
                  checked={formState.connectExisting}
                  description="If you already have an account with this payment provider, select this option to connect it."
                  label="Connect an existing account"
                  onChange={() =>
                    setFormState({
                      ...formState,
                      connectExisting: !formState.connectExisting
                    })
                  }
                />
              </AppStack>
            )}

          {showProviderAccountInputFields && (
            <AppStack style={{ gap: 8 }}>
              <TextInput
                disabled={
                  formState.isLoading ||
                  !!regAssociation?.regAssociationPaymentProvider
                }
                label={sportspaySelected ? 'SportsPay TERMID' : 'Stripe Payee'}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    accountId: e.currentTarget.value
                  })
                }
                required
                value={formState.accountId}
              />

              {sportspaySelected && (
                <TextInput
                  disabled={formState.isLoading}
                  label="Password"
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      accountPassword: e.currentTarget.value
                    })
                  }
                  required
                  type="password"
                  value={formState.accountPassword}
                />
              )}
            </AppStack>
          )}

          {showProviderAccountInputFields && sportspaySelected && (
            <>
              <Divider />

              <AppStack style={{ gap: 8 }}>
                <AppStack style={{ gap: 20 }}>
                  <AppStack style={{ gap: 8 }}>
                    <AppStack style={{ gap: 0 }}>
                      <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                        Service fees
                      </AppText>
                      <AppText style={{ fontSize: 14, color: '#666' }}>
                        Until these values are properly set your reporting may
                        be inaccurate.
                      </AppText>
                    </AppStack>

                    <Checkbox
                      checked={formState.serviceFees.useDefaultServiceFee}
                      disabled={formState.isLoading}
                      label="Use the default service fee of 2.54%"
                      onChange={(e) =>
                        setFormState({
                          ...formState,
                          serviceFees: {
                            ...formState.serviceFees,
                            useDefaultServiceFee: e.currentTarget.checked
                          }
                        })
                      }
                      style={{ fontWeight: 500 }}
                    />
                  </AppStack>

                  {!formState.serviceFees.useDefaultServiceFee && (
                    <AppStack style={{ gap: 8 }}>
                      <AppText
                        style={{
                          fontSize: 14,
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}
                      >
                        Enter the service fees for this payment provider.
                      </AppText>
                      <NumberFormat
                        allowNegative={false}
                        customInput={TextInput}
                        decimalScale={2}
                        disabled={formState.isLoading}
                        fixedDecimalScale
                        label="Flat Rate Fee"
                        onValueChange={(e) =>
                          setFormState({
                            ...formState,
                            serviceFees: {
                              ...formState.serviceFees,
                              flatRateFee: e.floatValue * 100
                            }
                          })
                        }
                        placeholder="$0.00"
                        prefix="$"
                        required
                        rightSection={
                          currency && (
                            <AppText
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#999',
                                paddingRight: 5
                              }}
                            >
                              {currency}
                            </AppText>
                          )
                        }
                        style={{ flex: 1 }}
                        thousandSeparator
                        value={
                          formState.serviceFees.flatRateFee === null
                            ? null
                            : formState.serviceFees.flatRateFee / 100
                        }
                      />
                      <NumberFormat
                        allowNegative={false}
                        customInput={TextInput}
                        decimalScale={2}
                        disabled={formState.isLoading}
                        label="Percentage Rate Fee"
                        onValueChange={(e) =>
                          setFormState({
                            ...formState,
                            serviceFees: {
                              ...formState.serviceFees,
                              percentageRateFee: e.floatValue
                            }
                          })
                        }
                        placeholder="2.54%"
                        required
                        style={{ flex: 1 }}
                        suffix="%"
                        value={formState.serviceFees.percentageRateFee}
                      />
                    </AppStack>
                  )}

                  <Alert
                    color="yellow"
                    style={{ marginTop: 8 }}
                    variant="outline"
                  >
                    <AppStack style={{ gap: 8 }}>
                      <AppText weight={500}>Rebase existing fees</AppText>
                      <Checkbox
                        checked={formState.serviceFees.rebasePaymentFees}
                        description="This will update all existing payment service fees to the new service fees"
                        disabled={formState.isLoading}
                        label="Rebase existing payment service fees"
                        onChange={() => {
                          if (formState.serviceFees.rebasePaymentFees) {
                            setFormState({
                              ...formState,
                              serviceFees: {
                                ...formState.serviceFees,
                                rebasePaymentFees: false
                              }
                            });
                          }
                          else {
                            setFormState({
                              ...formState,
                              showConfirmRebaseModal: true
                            });
                          }
                        }}
                        style={{ fontWeight: 500 }}
                      />
                    </AppStack>
                  </Alert>
                </AppStack>
              </AppStack>
            </>
          )}
        </AppStack>
      )}
      <ResponsiveModal
        formSectionProps={{
          onCancel: () =>
            setFormState({
              ...formState,
              showConfirmRebaseModal: false,
              serviceFees: {
                ...formState.serviceFees,
                rebasePaymentFees: false
              }
            }),
          onSubmit: () =>
            setFormState({
              ...formState,
              showConfirmRebaseModal: false,
              serviceFees: {
                ...formState.serviceFees,
                rebasePaymentFees: true
              }
            }),
          submitTitle: 'Confirm'
        }}
        isNewModalStyle
        isOpen={formState.showConfirmRebaseModal}
        onClose={() =>
          setFormState({
            ...formState,
            showConfirmRebaseModal: false,
            serviceFees: {
              ...formState.serviceFees,
              rebasePaymentFees: false
            }
          })
        }
        title="Rebase Payment Service Fees?"
      >
        <AppStack style={{ gap: 16 }}>
          <AppText style={{ fontSize: 14 }}>
            This will update the service fee applied to all existing payments to
            match the new service fee.
          </AppText>

          <AppText style={{ fontSize: 14 }}>
            Only select this option if you want previous payments to reflect the
            new service fee. Do not select it if existing payments used a
            different fee.
          </AppText>

          <AppText style={{ fontWeight: 500, fontSize: 14 }}>
            This action is permanent and cannot be undone.
          </AppText>

          <Checkbox
            label="I understand this action cannot be undone and will update existing service fees."
            required
          />
        </AppStack>
      </ResponsiveModal>
    </ResponsiveModal>
  );
};

FinancialPayeeOnboardingModal.propTypes = {
  fkPaymentProviderType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  regAssociation: PropTypes.object
};

export default FinancialPayeeOnboardingModal;
